import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SectionData } from "../../types/Response/CustomerResponse";
import { useAnalytics } from "../../utils/Customer/useAnalytics";

interface CarouselProps {
  items: SectionData[];
  onItemClick?: (item: SectionData) => void;
}

const Carousel: React.FC<CarouselProps> = ({ items, onItemClick }) => {
  const { trackBannerView, trackBannerClick } = useAnalytics();

  // ============================================================
  // SAFE ITEMS
  // ============================================================

  const safeItems = Array.isArray(items) ? items : [];
  const totalItems = safeItems.length;

  // ============================================================
  // PERFORMANCE & IMAGE PRE-LOADING STATES
  // ============================================================

  const [loadedCount, setLoadedCount] = useState(0);
  const [totalAssetsToLoad, setTotalAssetsToLoad] = useState(0);
  const [isFullyReady, setIsFullyReady] = useState(false);

  // ============================================================
  // SLIDER INTERACTION STATES
  // ============================================================

  const [currentIndex, setCurrentIndex] = useState(0);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ============================================================
  // ANALYTICS
  // ============================================================

  /**
   * Prevent the same banner from being tracked repeatedly
   * by this particular carousel instance.
   */
  const lastTrackedViewIdRef = useRef<number | null>(null);

  /**
   * Whether the carousel is currently visible in the
   * browser viewport.
   */
  const [isCarouselVisible, setIsCarouselVisible] = useState(false);

  /**
   * Root element observed by IntersectionObserver.
   */
  const carouselRef = useRef<HTMLDivElement | null>(null);

  // ============================================================
  // IMAGE PRELOADING
  // ============================================================

  useEffect(() => {
    if (totalItems === 0) {
      setLoadedCount(0);
      setTotalAssetsToLoad(0);
      setIsFullyReady(true);
      return;
    }

    let isMounted = true;

    setLoadedCount(0);
    setIsFullyReady(false);
    setCurrentIndex(0);

    // ----------------------------------------------------------
    // Calculate actual number of assets
    // ----------------------------------------------------------

    let assetsCount = 0;

    safeItems.forEach((item) => {
      if (item?.imageUrl) {
        assetsCount++;
      }

      if (item?.mobileImageUrl) {
        assetsCount++;
      }

      // If neither image exists, count the item as ready.
      if (!item?.imageUrl && !item?.mobileImageUrl) {
        assetsCount++;
      }
    });

    setTotalAssetsToLoad(assetsCount);

    // ----------------------------------------------------------
    // Nothing to preload
    // ----------------------------------------------------------

    if (assetsCount === 0) {
      setIsFullyReady(true);
      return;
    }

    let loadedAssets = 0;

    const checkAssetLoaded = () => {
      loadedAssets++;

      if (!isMounted) {
        return;
      }

      setLoadedCount(loadedAssets);
    };

    // ----------------------------------------------------------
    // Preload all images
    // ----------------------------------------------------------

    safeItems.forEach((item) => {
      const sources = [item?.imageUrl, item?.mobileImageUrl].filter(
        Boolean,
      ) as string[];

      // No image
      if (sources.length === 0) {
        checkAssetLoaded();
        return;
      }

      sources.forEach((src) => {
        const img = new Image();

        img.onload = checkAssetLoaded;
        img.onerror = checkAssetLoaded;

        img.src = src;
      });
    });

    return () => {
      isMounted = false;
    };
  }, [items, totalItems]);

  // ============================================================
  // CHECK WHETHER ALL IMAGES ARE READY
  // ============================================================

  useEffect(() => {
    if (totalItems === 0) {
      setIsFullyReady(true);
      return;
    }

    if (totalAssetsToLoad > 0 && loadedCount >= totalAssetsToLoad) {
      setIsFullyReady(true);
    }
  }, [loadedCount, totalAssetsToLoad, totalItems]);

  // ============================================================
  // INTERSECTION OBSERVER
  //
  // Only consider a banner "viewed" when the carousel itself
  // is actually visible in the browser viewport.
  // ============================================================

  useEffect(() => {
    const element = carouselRef.current;

    if (!element) {
      console.log("Analytics: carousel element not available");

      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;

        console.log(
          "Analytics: carousel visibility:",
          visible,
          "ratio:",
          entry.intersectionRatio,
        );

        setIsCarouselVisible(visible);
      },
      {
        /**
         * 0.5 means at least approximately 50% of the
         * carousel must be visible.
         */
        threshold: 0.5,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  // ============================================================
  // AUTOPLAY MANAGEMENT
  // ============================================================

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (!isFullyReady || totalItems <= 1) {
      return;
    }

    resetTimeout();

    timeoutRef.current = setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === totalItems - 1 ? 0 : prevIndex + 1,
      );
    }, 4000);

    return () => {
      resetTimeout();
    };
  }, [currentIndex, isFullyReady, totalItems]);

  // ============================================================
  // ANALYTICS: BANNER VIEW
  //
  // Conditions:
  //
  // 1. Carousel must be fully loaded
  // 2. Carousel must be visible
  // 3. There must be a valid banner
  // 4. Same banner must not already be tracked by this
  //    carousel instance
  //
  // useAnalytics additionally protects against duplicate
  // session-level tracking.
  // ============================================================

  useEffect(() => {
    if (!isFullyReady) {
      return;
    }

    if (!isCarouselVisible) {
      return;
    }

    if (totalItems === 0) {
      return;
    }

    const currentItem = safeItems[currentIndex];

    if (!currentItem?.id) {
      return;
    }

    const bannerId = Number(currentItem.id);

    if (!bannerId) {
      return;
    }

    // ----------------------------------------------------------
    // Prevent duplicate tracking for the same banner
    // ----------------------------------------------------------

    if (lastTrackedViewIdRef.current === bannerId) {
      return;
    }

    lastTrackedViewIdRef.current = bannerId;

    console.log("Analytics: tracking BannerView:", bannerId);

    trackBannerView(bannerId);
  }, [
    currentIndex,
    isFullyReady,
    isCarouselVisible,
    totalItems,
    trackBannerView,
  ]);

  // ============================================================
  // EMPTY STATE
  // ============================================================

  if (totalItems === 0) {
    return null;
  }

  // ============================================================
  // NAVIGATION CONTROLS
  // ============================================================

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();

    setCurrentIndex((prev) => (prev === 0 ? totalItems - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();

    setCurrentIndex((prev) => (prev === totalItems - 1 ? 0 : prev + 1));
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div
      ref={carouselRef}
      className={`w-full relative group transition-opacity duration-500 ease-in-out ${
        isFullyReady ? "opacity-100" : "opacity-0"
      }`}
    >
      {/* ======================================================
          SLIDER FRAME
      ====================================================== */}

      <div className="relative w-full aspect-[16/9] md:aspect-[16/7] lg:aspect-[16/5] overflow-hidden shadow-md bg-transparent">
        {/* ====================================================
            HARDWARE ACCELERATED SLIDING TRACK
        ==================================================== */}

        <div
          className="flex w-full h-full transition-transform duration-700 ease-out will-change-transform"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {safeItems.map((item, index) => (
            <div
              key={item.id || index}
              className="w-full h-full flex-shrink-0 relative cursor-pointer"
              onClick={() => {
                // ------------------------------------------------
                // Analytics: Banner Click
                // ------------------------------------------------

                if (item?.id) {
                  const bannerId = Number(item.id);

                  if (bannerId) {
                    console.log("Analytics: tracking BannerClick:", bannerId);

                    trackBannerClick(bannerId);
                  }
                }

                // ------------------------------------------------
                // Existing click behavior
                // ------------------------------------------------

                onItemClick?.(item);
              }}
            >
              {/* =================================================
                    RESPONSIVE PICTURE ELEMENT
                ================================================= */}

              <picture className="w-full h-full block">
                {/* Mobile image */}
                {item.mobileImageUrl && (
                  <source
                    media="(max-width: 767px)"
                    srcSet={item.mobileImageUrl}
                  />
                )}

                {/* Desktop / fallback image */}
                <img
                  src={item.imageUrl}
                  alt={item.title || `Slide ${index + 1}`}
                  decoding="sync"
                  className="w-full h-full object-cover select-none transform translate-z-0"
                />
              </picture>

              {/* =================================================
                    TEXT OVERLAY
                ================================================= */}

              {(item.title || item.subTitle || item.description) && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent flex flex-col justify-end p-8 md:p-16 text-white space-y-2">
                  {item.title && (
                    <h3 className="text-xl md:text-4xl font-black tracking-tight leading-tight max-w-xl">
                      {item.title}
                    </h3>
                  )}

                  {(item.subTitle || item.description) && (
                    <p className="text-xs md:text-base text-white/80 max-w-md font-medium">
                      {item.subTitle || item.description}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ======================================================
            LEFT / RIGHT ARROWS
        ====================================================== */}

        {totalItems > 1 && (
          <>
            {/* Previous */}
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white/80 backdrop-blur-md text-logoHeading rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-md hover:bg-white hover:scale-105 active:scale-95 z-10"
              aria-label="Previous banner"
            >
              <ChevronLeft size={24} strokeWidth={2.5} />
            </button>

            {/* Next */}
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white/80 backdrop-blur-md text-logoHeading rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-md hover:bg-white hover:scale-105 active:scale-95 z-10"
              aria-label="Next banner"
            >
              <ChevronRight size={24} strokeWidth={2.5} />
            </button>
          </>
        )}

        {/* ======================================================
            DOT INDICATORS
        ====================================================== */}

        {totalItems > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {safeItems.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentIndex === i
                    ? "w-6 bg-white shadow-sm"
                    : "w-2 bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Carousel;
